// /quote/:id/authorize?pt=<token> — the account-less partner's
// post-signing landing page (ADR-009 §4). Reached via the return_url
// the portal supplied to Nexus at request-signature time; authorized by
// the quote-scoped pay token in the `pt` query param (NOT a login).
//
// From here the partner can pay by credit card now — but ONLY while
// CARD_PAYMENT_ENABLED is on. Card is disabled for now (Argo isn't taking
// cards through the portal yet), so the page confirms the signed quote and
// tells the partner their Argo contact will follow up; AR invoices after
// printing. There is no "request an invoice" action (an approved quote is
// just a quote). After a card payment we poll until status='paid' and show
// the estimated turnaround. The signed quote PDF is downloadable throughout.

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import type { CheckoutResponse } from '../api/submission.js';
import * as payApi from '../api/pay.js';
import { ANALYTICS_EVENTS, trackEvent } from '../lib/analytics/index.js';
import { PaymentForm } from '../components/payment/PaymentForm.js';
import { CARD_PAYMENT_ENABLED } from '../featureFlags.js';
import { formatCurrency, formatDate as formatDateIntl } from '../i18n/formatters.js';
import { SUPPORT_EMAIL, SUPPORT_PHONE } from '../pricingCopy.js';

// General Argo contact fallback, rendered wherever no specific sales rep is
// on file — so no partner-facing message ever strands the customer without a
// way to reach us (email + phone in every handoff).
function SupportContactLine(): JSX.Element {
  const { t } = useTranslation(['status', 'common']);
  return (
    <p style={{ margin: '8px 0 0' }}>
      <Trans
        i18nKey="authorize.support.contactLine"
        t={t}
        values={{ email: SUPPORT_EMAIL, phone: SUPPORT_PHONE }}
        components={[
          <a key="email" href={`mailto:${SUPPORT_EMAIL}`} />,
          <a key="phone" href={`tel:${SUPPORT_PHONE.replace(/[^0-9+]/g, '')}`} />,
        ]}
      />
    </p>
  );
}

function formatMoney(amount: number | null, currency: string | null): string {
  if (amount === null) return '—';
  return formatCurrency(amount, currency ?? 'USD');
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return formatDateIntl(d, { year: 'numeric', month: 'long', day: 'numeric' });
}

export function QuoteAuthorize(): JSX.Element {
  useParams(); // /quote/:id/authorize — id is informational; the pay token is authoritative.
  const [searchParams] = useSearchParams();
  const payToken = searchParams.get('pt');
  const { t } = useTranslation(['status', 'common']);

  const [mode, setMode] = useState<'choose' | 'card'>('choose');
  const [checkout, setCheckout] = useState<CheckoutResponse | null>(null);
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const payQuery = useQuery({
    queryKey: ['pay-quote', payToken],
    queryFn: () => payApi.getPayQuote(payToken!),
    enabled: Boolean(payToken),
    // Poll while waiting on payment confirmation, OR while the signature
    // is still pending (the doc_service notice flips it server-side; the
    // GET also does an on-return status fallback). Stop once resolved.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.ok) return false;
      const s = data.quote.status;
      if (paymentInitiated && s !== 'paid') return 1500;
      if (s === 'awaiting_signature') return 2000;
      return false;
    },
  });

  if (!payToken) {
    return (
      <div className="card">
        <div className="banner banner--error">
          <strong>{t('authorize.missingToken.title')}</strong> {t('authorize.missingToken.body')}
        </div>
        <SupportContactLine />
      </div>
    );
  }

  if (payQuery.isLoading) return <p className="page-loading">{t('authorize.loadingQuote')}</p>;

  if (!payQuery.data || !payQuery.data.ok) {
    const msg = payQuery.data && !payQuery.data.ok ? payQuery.data.error.message : null;
    return (
      <div className="card">
        <div className="banner banner--error">
          <strong>{t('authorize.invalidToken.title')}</strong>
          {msg ? <> {msg}</> : null} {t('authorize.invalidToken.body')}
        </div>
        <SupportContactLine />
      </div>
    );
  }

  const quote = payQuery.data.quote;

  async function startCard(): Promise<void> {
    setActionError(null);
    try {
      const result = await payApi.startPayCheckout(payToken!);
      // Same event, different surface: the tokenized partner pay link, which
      // has no signed-in session behind it.
      trackEvent(ANALYTICS_EVENTS.payment_started, { surface: 'pay_link' });
      setCheckout(result);
      setMode('card');
    } catch {
      setActionError(t('authorize.error.startCardFailed'));
    }
  }

  async function downloadSigned(): Promise<void> {
    setDownloading(true);
    try {
      const url = await payApi.getSignedDocumentUrl(payToken!);
      if (url) window.open(url, '_blank', 'noopener');
      else setActionError(t('authorize.error.documentUnavailable'));
    } finally {
      setDownloading(false);
    }
  }

  // ── Paid ────────────────────────────────────────────────────────────
  if (quote.status === 'paid') {
    const finish = formatDate(quote.requested_finish_date);
    const projectTitle = quote.project_title ?? t('authorize.paid.orderFallback');
    return (
      <div className="card">
        <div className="banner banner--success">
          <strong>{t('authorize.paid.title')}</strong>
        </div>
        <p>
          <Trans
            i18nKey="authorize.paid.body"
            t={t}
            values={{ title: projectTitle }}
            components={[<strong key="title" />]}
          />
        </p>
        <p>
          {finish ? (
            <Trans
              i18nKey="authorize.paid.deliveryEstimate"
              t={t}
              values={{ date: finish }}
              components={[<strong key="date" />]}
            />
          ) : (
            t('authorize.paid.deliveryUnknown')
          )}{' '}
          {quote.sales_rep_name ? (
            quote.sales_rep_email ? (
              <Trans
                i18nKey="authorize.paid.repQuestionsWithEmail"
                t={t}
                values={{ name: quote.sales_rep_name, email: quote.sales_rep_email }}
                components={[<a key="email" href={`mailto:${quote.sales_rep_email}`} />]}
              />
            ) : (
              t('authorize.paid.repQuestionsNoEmail', { name: quote.sales_rep_name })
            )
          ) : (
            t('authorize.paid.contactFallback')
          )}
        </p>
        {!quote.sales_rep_email && <SupportContactLine />}
        <p style={{ color: 'var(--color-text)' }}>
          <Trans
            i18nKey="authorize.paid.reference"
            t={t}
            values={{ id: quote.id }}
            components={[<strong key="id" />]}
          />
        </p>
        {quote.has_signed_document && (
          <p>
            <button type="button" onClick={() => void downloadSigned()} disabled={downloading}>
              {t('authorize.actions.downloadSigned')}
            </button>
          </p>
        )}
      </div>
    );
  }

  // ── Pending sales approval ──────────────────────────────────────────
  if (quote.status === 'pending_approval') {
    return (
      <div className="card">
        <div className="banner banner--success">
          <strong>{t('authorize.pendingApproval.title')}</strong>
        </div>
        <p>{t('authorize.pendingApproval.body')}</p>
        {quote.sales_rep_email ? (
          <p>
            <Trans
              i18nKey="authorize.pendingApproval.contactRep"
              t={t}
              values={{ email: quote.sales_rep_email }}
              components={[<a key="email" href={`mailto:${quote.sales_rep_email}`} />]}
            />
          </p>
        ) : (
          <SupportContactLine />
        )}
      </div>
    );
  }

  // ── Not yet in a settle-able state (e.g. still awaiting signature) ──
  if (!quote.can_pay_by_card) {
    return (
      <div className="card">
        <div className="banner banner--warn">{t('authorize.notReady.message')}</div>
        <SupportContactLine />
      </div>
    );
  }

  // ── Signed & ready: pay by card (only while the flag is on), else the
  //    quote stands and the Argo contact follows up ────────────────────
  const cardAvailable = CARD_PAYMENT_ENABLED && quote.can_pay_by_card;
  const finish = formatDate(quote.requested_finish_date);
  const amountDue = formatMoney(quote.total_sale_amount, quote.currency);
  return (
    <>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>{quote.project_title ?? t('authorize.ready.titleFallback')}</h2>
        <p>
          <Trans
            i18nKey="authorize.ready.amountDue"
            t={t}
            values={{ amount: amountDue }}
            components={[<strong key="amount" />]}
          />
          {quote.terms ? t('authorize.ready.termsSuffix', { terms: quote.terms }) : null}
        </p>
        {finish && (
          <p style={{ color: 'var(--color-text-muted)' }}>
            {t('authorize.ready.requestedDelivery', { date: finish })}
          </p>
        )}
        {quote.sales_rep_name && (
          <p style={{ color: 'var(--color-text-muted)' }}>
            {quote.sales_rep_email ? (
              <Trans
                i18nKey="authorize.ready.repContactWithEmail"
                t={t}
                values={{ name: quote.sales_rep_name, email: quote.sales_rep_email }}
                components={[<a key="email" href={`mailto:${quote.sales_rep_email}`} />]}
              />
            ) : (
              t('authorize.ready.repContact', { name: quote.sales_rep_name })
            )}
          </p>
        )}
        {quote.has_signed_document && (
          <p>
            <button type="button" onClick={() => void downloadSigned()} disabled={downloading}>
              {t('authorize.actions.downloadSigned')}
            </button>
          </p>
        )}
        {actionError && <div className="banner banner--error">{actionError}</div>}
        {(quote.conditions_text || quote.tax_disclosure) && (
          <details style={{ marginTop: 'var(--space-3)' }}>
            <summary>{t('authorize.ready.termsConditionsSummary')}</summary>
            {quote.conditions_text && (
              <p style={{ fontSize: '0.85em', color: 'var(--color-text-muted)' }}>{quote.conditions_text}</p>
            )}
            {quote.tax_disclosure && (
              <p style={{ fontSize: '0.85em', color: 'var(--color-text-muted)' }}>{quote.tax_disclosure}</p>
            )}
          </details>
        )}
      </div>

      {mode === 'choose' && (
        <div className="card">
          {cardAvailable ? (
            <>
              <h3 style={{ marginTop: 0 }}>{t('authorize.ready.payPrompt')}</h3>
              <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                <button type="button" className="primary" onClick={() => void startCard()}>
                  {t('authorize.actions.payByCard')}
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>{t('authorize.ready.allSetTitle')}</h3>
              <p style={{ marginBottom: 0, color: 'var(--color-text-muted)' }}>
                {t('authorize.ready.allSetBody')}
              </p>
            </>
          )}
        </div>
      )}

      {mode === 'card' && checkout && (
        <PaymentForm
          checkout={checkout}
          onPaymentInitiated={() => setPaymentInitiated(true)}
          simulateMockSuccess={(c) => payApi.simulatePayMockSuccess(payToken!, c)}
        />
      )}
      {mode === 'card' && !checkout && <p>{t('authorize.ready.preparingCheckout')}</p>}
    </>
  );
}
